import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SkillsShowcase = () => {
  const [activeCategory, setActiveCategory] = useState(0);

  const skillCategories = [
    {
      id: 0,
      title: "Technical Expertise",
      subtitle: "Core Development Skills",
      description: "Proficient in modern web technologies with a focus on scalable, maintainable solutions.",
      icon: "Code",
      color: "from-primary to-accent",
      skills: [
        { name: "JavaScript/TypeScript", level: 95, icon: "Zap", experience: "2+ years" },
        { name: "React/Next.js", level: 90, icon: "Layers", experience: "2+ years" },
        { name: "Node.js/Express", level: 88, icon: "Server", experience: "2+ years" },
        { name: "Python/Django", level: 50, icon: "Code2", experience: "1+ years" },
        { name: "Database Design", level: 90, icon: "Database", experience: "2+ years" },
        { name: "Cloud Platforms", level: 45, icon: "Cloud", experience: "1+ years" }
      ]
    },
    {
      id: 1,
      title: "Leadership & Mentorship",
      subtitle: "People Development Skills",
      description: "Building and nurturing communities while developing the next generation of developers.",
      icon: "Users",
      color: "from-success to-primary",
      skills: [
        { name: "Community Building", level: 98, icon: "Users", experience: "2+ years" },
        { name: "Technical Mentoring", level: 95, icon: "BookOpen", experience: "2+ years" },
        { name: "Workshop Facilitation", level: 90, icon: "Presentation", experience: "2+ years" },
        { name: "Curriculum Development", level: 88, icon: "FileText", experience: "2+ years" },
        { name: "Public Speaking", level: 85, icon: "Mic", experience: "1+ years" },
        { name: "Team Leadership", level: 87, icon: "Crown", experience: "2+ years" }
      ]
    },
    {
      id: 2,
      title: "Cultural Bridge-Building",
      subtitle: "Cross-Cultural Communication",
      description: "Connecting Cameroonian and African talent with global opportunities while preserving local context and values.",
      icon: "Globe",
      color: "from-accent to-secondary",
      skills: [
        { name: "Cross-Cultural Communication", level: 92, icon: "MessageCircle", experience: "2+ years" },
        { name: "International Collaboration", level: 88, icon: "Handshake", experience: "3+ years" },
        { name: "Remote Team Management", level: 90, icon: "Monitor", experience: "2+ years" },
        { name: "Cultural Sensitivity", level: 95, icon: "Heart", experience: "Lifetime" },
        { name: "Global Market Understanding", level: 85, icon: "TrendingUp", experience: "3+ years" },
        { name: "Localization Strategy", level: 82, icon: "MapPin", experience: "2+ years" }
      ]
    },
    {
      id: 3,
      title: "Business & Strategy",
      subtitle: "Strategic Thinking",
      description: "Understanding the intersection of technology and business to create meaningful impact.",
      icon: "Target",
      color: "from-secondary to-success",
      skills: [
        { name: "Product Strategy", level: 85, icon: "Target", experience: "2+ years" },
        { name: "Project Management", level: 90, icon: "CheckSquare", experience: "2+ years" },
        { name: "Stakeholder Management", level: 88, icon: "Users2", experience: "2+ years" },
        { name: "Business Analysis", level: 82, icon: "BarChart", experience: "2+ years" },
        { name: "Process Optimization", level: 87, icon: "Zap", experience: "2+ years" },
        { name: "Innovation Strategy", level: 85, icon: "Lightbulb", experience: "2+ years" }
      ]
    }
  ];

  const getSkillColor = (level) => {
    if (level >= 90) return 'bg-success';
    if (level >= 80) return 'bg-primary';
    if (level >= 70) return 'bg-accent';
    return 'bg-warning';
  };

  const getSkillLabel = (level) => {
    if (level >= 90) return 'Expert';
    if (level >= 80) return 'Advanced';
    if (level >= 70) return 'Intermediate';
    return 'Developing';
  };

  return (
    <section className="py-20 bg-gradient-to-br from-background to-muted/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-1 bg-primary rounded-full"></div>
            <span className="text-primary font-medium tracking-wider uppercase text-sm">
              Skills & Expertise
            </span>
            <div className="w-12 h-1 bg-primary rounded-full"></div>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-secondary mb-6">
            Beyond
            <span className="text-gradient-gold"> Code</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            A comprehensive skill set that combines technical expertise with human-centered leadership and cultural intelligence.
          </p>
        </div>

        {/* Category Navigation */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {skillCategories?.map((category, index) => (
            <button
              key={category?.id}
              onClick={() => setActiveCategory(index)}
              className={`p-6 rounded-xl text-left transition-all duration-300 ${
                activeCategory === index
                  ? 'bg-white shadow-elevated transform scale-105'
                  : 'bg-white/50 hover:bg-white hover:shadow-lg'
              }`}
            >
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${category?.color} flex items-center justify-center mb-4`}>
                <Icon name={category?.icon} size={24} className="text-white" />
              </div>
              <h3 className="text-lg font-semibold text-secondary mb-2">
                {category?.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {category?.subtitle}
              </p>
            </button>
          ))}
        </div>

        {/* Active Category Content */}
        <div className="animate-fade-in">
          {/* Category Header */}
          <div className="text-center mb-12">
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${skillCategories?.[activeCategory]?.color} flex items-center justify-center mx-auto mb-6 shadow-lg`}>
              <Icon name={skillCategories?.[activeCategory]?.icon} size={36} className="text-white" />
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-secondary mb-4">
              {skillCategories?.[activeCategory]?.title}
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {skillCategories?.[activeCategory]?.description}
            </p>
          </div>

          {/* Skills Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skillCategories?.[activeCategory]?.skills?.map((skill, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-elegant hover:shadow-elevated transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name={skill?.icon} size={20} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-secondary">{skill?.name}</h4>
                      <p className="text-sm text-muted-foreground">{skill?.experience}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-secondary">{skill?.level}%</div>
                    <div className={`text-xs px-2 py-1 rounded-full text-white ${getSkillColor(skill?.level)}`}>
                      {getSkillLabel(skill?.level)}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-1000 ${getSkillColor(skill?.level)}`}
                      style={{ width: `${skill?.level}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Category Summary */}
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-elegant">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-secondary mb-2">
                  {skillCategories?.[activeCategory]?.skills?.length}
                </div>
                <div className="text-muted-foreground">Core Skills</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-secondary mb-2">
                  {Math.round(skillCategories?.[activeCategory]?.skills?.reduce((acc, skill) => acc + skill?.level, 0) / skillCategories?.[activeCategory]?.skills?.length)}%
                </div>
                <div className="text-muted-foreground">Average Proficiency</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-secondary mb-2">
                  {skillCategories?.[activeCategory]?.skills?.filter(skill => skill?.level >= 90)?.length}
                </div>
                <div className="text-muted-foreground">Expert Level</div>
              </div>
            </div>
          </div>
        </div>

        {/* Overall Skills Summary */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-secondary mb-6">
              Continuous Learning Journey
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Icon name="BookOpen" size={32} className="text-primary mx-auto" />
                <div className="text-lg font-semibold text-secondary">Always Learning</div>
                <div className="text-sm text-muted-foreground">New technologies and methodologies</div>
              </div>
              <div className="space-y-2">
                <Icon name="Users" size={32} className="text-accent mx-auto" />
                <div className="text-lg font-semibold text-secondary">Teaching Others</div>
                <div className="text-sm text-muted-foreground">Sharing knowledge and experience</div>
              </div>
              <div className="space-y-2">
                <Icon name="Target" size={32} className="text-success mx-auto" />
                <div className="text-lg font-semibold text-secondary">Goal Oriented</div>
                <div className="text-sm text-muted-foreground">Focused on measurable impact</div>
              </div>
              <div className="space-y-2">
                <Icon name="Heart" size={32} className="text-error mx-auto" />
                <div className="text-lg font-semibold text-secondary">Community First</div>
                <div className="text-sm text-muted-foreground">Building inclusive environments</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsShowcase;